'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Send } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import { useAuth } from '@/app/contexts/AuthContext';
import { useLanguage } from '@/lib/i18n';

export default function HeroSection() {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [mobileQuery, setMobileQuery] = useState('');
  const { user } = useAuth();
  const { t, isRTL } = useLanguage();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      if (!user) {
        router.push('/sign-up-login-screen');
        return;
      }
      router.push(`/ai-chat-interface?query=${encodeURIComponent(query.trim())}`);
    }
  };

  const handleMobileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mobileQuery.trim()) {
      if (!user) {
        router.push('/sign-up-login-screen');
        return;
      }
      router.push(`/ai-chat-interface?query=${encodeURIComponent(mobileQuery.trim())}`);
    }
  };

  return (
    <>
      {/* ── DESKTOP VERSION (hidden on mobile) ── */}
      <section
        className="relative min-h-screen hidden md:flex items-center overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #1a237e 0%, #1565c0 40%, #1976d2 70%, #42a5f5 100%)' }}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Mascot — absolutely positioned */}
        <div
          className={`absolute ${isRTL ? 'right-0' : 'left-0'} top-0 h-full flex items-end pointer-events-none select-none`}
          style={{ width: '45%' }}
        >
          <div className="relative w-full h-full">
            <AppImage
              src="/assets/images/dalili-1775932023691.png"
              alt="Dalili mascot"
              fill
              className="object-contain object-bottom"
              style={{ transform: isRTL ? 'translateX(30%)' : 'translateX(-30%)' }}
            />
          </div>
        </div>

        {/* Content */}
        <div
          className={`relative z-10 w-full max-w-xl px-8 py-16 flex flex-col ${isRTL ? 'items-end text-right' : 'items-start'} justify-center`}
          style={{ [isRTL ? 'marginRight' : 'marginLeft']: '40%' }}
        >
          <h1 className="sm:text-8xl font-black text-white tracking-tight mb-2 text-[176px]">
            Dalili
          </h1>
          <p className="sm:text-lg text-blue-100 font-medium mb-10 text-[17px]">
            {t.hero.subtitle}
          </p>
          <form
            onSubmit={handleSubmit}
            className="w-full flex items-center bg-white rounded-2xl shadow-xl overflow-hidden px-4 py-2 gap-2"
          >
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.hero.placeholder}
              dir={isRTL ? 'rtl' : 'ltr'}
              className="flex-1 bg-transparent text-gray-800 placeholder-gray-400 text-sm outline-none py-2"
            />
            <button
              type="submit"
              className="flex items-center justify-center w-10 h-10 rounded-xl hover:opacity-90 transition-colors flex-shrink-0"
              style={{ backgroundColor: '#1a237e' }}
            >
              <Send size={16} className="text-white" />
            </button>
          </form>
        </div>
      </section>

      {/* ── MOBILE VERSION (hidden on desktop) ── */}
      <section
        className="md:hidden flex flex-col justify-between items-center min-h-screen px-6 pt-10 pb-8"
        style={{ backgroundColor: '#0A46B3' }}
      >
        {/* Top: Full mascot centered */}
        <div className="flex-1 flex items-center justify-center w-full">
          <div className="relative w-64 h-80">
            <AppImage
              src="/assets/images/dalili-1775932023691.png"
              alt="Dalili mascot"
              fill
              className="object-contain"
            />
          </div>
        </div>

        {/* Center: Title + Subtitle */}
        <div className="flex flex-col items-center text-center mb-8">
          <h1 className="text-5xl font-black text-white tracking-tight mb-3" style={{ fontFamily: 'Urbanist, sans-serif' }}>
            Dalili
          </h1>
          <p className="text-sm text-blue-100 font-medium max-w-xs leading-relaxed">
            ton accompagnateur en démarches administratives en france
          </p>
        </div>

        {/* Bottom: Chat bar */}
        <form
          onSubmit={handleMobileSubmit}
          className="w-full flex items-center bg-white rounded-2xl shadow-xl overflow-hidden px-4 py-2 gap-2"
        >
          <input
            type="text"
            value={mobileQuery}
            onChange={(e) => setMobileQuery(e.target.value)}
            placeholder={t.hero.placeholder}
            className="flex-1 bg-transparent text-gray-800 placeholder-gray-400 text-sm outline-none py-2"
          />
          <button
            type="submit"
            className="flex items-center justify-center w-10 h-10 rounded-xl hover:opacity-90 transition-colors flex-shrink-0"
            style={{ backgroundColor: '#0A46B3' }}
          >
            <Send size={16} className="text-white" />
          </button>
        </form>
      </section>
    </>
  );
}